import { BackgroundJobKind, IBackgroundJob, IBackgroundJobStarted } from '@models/backgroundJobs/IBackgroundJob';
import {
    awaitBackgroundJob,
    BACKGROUND_UNAVAILABLE_TEXT,
    BackgroundJobWaitStore,
    startBackgroundJob,
} from './awaitBackgroundJob';
import { BackgroundJobAction, backgroundJobKind, COMMON_STAGE_LABELS, jobActions } from './backgroundJobKinds';
import { BackgroundJobsStore, backgroundJobsStore } from './BackgroundJobsStore';
import { jobProgressLabel, jobStateLabel } from './backgroundJobView';

export interface IBackgroundJobResultFile {
    name: string;
    /** Скачать файл: кнопкой в итоге окна, а без итога в окне — сразу по приходу ответа. Промис — пока скачивается. */
    download: () => unknown;
}

/** Итог работы, которую ждёт окно: задачи или запроса без неё. */
export interface IBackgroundJobWaitOutcome {
    success: boolean;
    /** Задачу отменили (например, из колокольчика): результата нет, но и ошибки нет. */
    cancelled?: boolean;
    /** Сообщение сервера; пустая строка — сервер ничего не сказал. */
    message: string;
    /** Замечания успеха. */
    warnings?: string[];
    /** Сколько замечаний всего, включая не вошедшие в `warnings`; нет — столько, сколько в `warnings`. */
    warningsTotal?: number;
    /** Файл результата: отметки ошибок или итог работы. */
    file?: IBackgroundJobResultFile;
    /** Действия итога сверх файла — из реестра видов. */
    actions?: BackgroundJobAction[];
    /** Окно показало итог: уведомление о нём больше не нужно. */
    onShown?: () => void;
}

export interface IBackgroundJobWaitProgress {
    /** Что происходит сейчас («В очереди · следующая»); без него — общая подпись лоадера. */
    stage?: string;
    /** Этап без цифр хода («В очереди») — для live-области окна: цифры меняются на каждом опросе. Нет — `stage`. */
    stageName?: string;
    /** Работа идёт на сервере и без окна: окно можно закрыть, итог придёт уведомлением. */
    detachable: boolean;
}

export interface IBackgroundJobTaskContext {
    /** Окно закрыто или работа начата заново: перестать следить за ходом, работу на сервере не трогать. */
    signal: AbortSignal;
    onProgress: (progress: IBackgroundJobWaitProgress) => void;
}

/** Способ довести работу до итога. Отказ сети или HTTP — отклонённый промис: текст ошибки делает окно. */
export type BackgroundJobTask = (context: IBackgroundJobTaskContext) => Promise<IBackgroundJobWaitOutcome>;

/** Строка остатка, которую прежний сервер дописывал после предела замечаний: «…и ещё 1 997 замечаний». */
const WARNINGS_REST = /^…и ещё ([\d\s]+?)\s*замечани/u;

/**
 * Замечания задачи и их общее число. Число — `warningsTotal`; у прежнего сервера его нет, а остаток сверх предела
 * приходит последней строкой «…и ещё N замечаний»: она не замечание, её число прибавляется к показанным.
 */
export const jobWarnings = (
    job: Pick<IBackgroundJob, 'warnings' | 'warningsTotal'>,
): { warnings: string[]; total: number } => {
    const listed = job.warnings ?? [];
    const rest = WARNINGS_REST.exec(listed.at(-1) ?? '');
    const warnings = rest ? listed.slice(0, -1) : listed;
    const total = job.warningsTotal ?? warnings.length + (rest ? Number(rest[1].replace(/\D/g, '')) : 0);
    return { warnings, total: Math.max(total, warnings.length) };
};

/**
 * Какие действия итога показывать в окне: все, никаких или отобранные (окну внутри документа «Открыть документ» не
 * нужен, а ссылка на папку с созданным — нужна).
 */
export type BackgroundJobResultActions = boolean | ((action: BackgroundJobAction) => boolean);

/** Итог закончившейся задачи для окна. Файл результата — своей кнопкой, остальные действия — по `actions`. */
export const jobWaitOutcome = (
    job: IBackgroundJob,
    store: Pick<BackgroundJobsStore, 'downloadFile' | 'markShownInWindow'>,
    { actions = true }: { actions?: BackgroundJobResultActions } = {},
): IBackgroundJobWaitOutcome => {
    const file = job.hasResultFile
        ? {
              name: job.resultFileName || backgroundJobKind(job.kind).wait.resultFileName,
              download: () => store.downloadFile(job.jobId, job.kind),
          }
        : undefined;
    const onShown = () => store.markShownInWindow(job.jobId);
    const shown = typeof actions === 'function' ? actions : () => actions;
    const extra = jobActions(job).filter((action) => action.type !== 'download' && shown(action));
    const withActions = extra.length ? { actions: extra } : {};

    if (job.status === 'cancelled') return { success: false, cancelled: true, message: '', onShown };
    if (job.status === 'succeeded') {
        const { warnings, total } = jobWarnings(job);
        return {
            success: true,
            message: job.summary?.trim() || job.message || '',
            warnings,
            warningsTotal: total,
            file,
            ...withActions,
            onShown,
        };
    }
    return { success: false, message: job.error?.trim() || job.message || '', file, ...withActions, onShown };
};

export interface ICreateBackgroundJobTaskOptions {
    kind: BackgroundJobKind;
    store?: BackgroundJobWaitStore;
    /** Фона нет (404 старта, 400 `runInBackground`) — та же работа без задачи; без него — отказ. */
    fallback?: BackgroundJobTask;
    /** Действия итога в окне (открыть документ, ссылку): все, никаких или отобранные. */
    resultActions?: BackgroundJobResultActions;
}

/**
 * Работа фоновой задачей: старт (`start` — POST вида → `{ jobId }`), этапы и прогресс, итог. Отказы старта — итог
 * с понятным текстом; выключенный фон — `fallback`.
 */
export const createBackgroundJobTask =
    (
        start: () => Promise<IBackgroundJobStarted>,
        { kind, store = backgroundJobsStore, fallback, resultActions = true }: ICreateBackgroundJobTaskOptions,
    ): BackgroundJobTask =>
    async (context) => {
        const { signal, onProgress } = context;
        const texts = backgroundJobKind(kind).wait;
        onProgress({ stage: texts.startLabel, detachable: false });

        const started = await startBackgroundJob(start, texts);
        if (started.type === 'fallback') {
            if (!fallback) return { success: false, message: BACKGROUND_UNAVAILABLE_TEXT };
            // Окно ушло, пока шёл старт: работа без задачи началась бы заново, а итог её никто бы не увидел.
            if (signal.aborted) return new Promise<never>(() => {});
            onProgress({ detachable: false });
            return fallback(context);
        }
        if (started.type === 'failed') return { success: false, message: started.message };

        if (!signal.aborted) onProgress({ stage: COMMON_STAGE_LABELS.queue, detachable: true });
        const job = await awaitBackgroundJob(started.jobId, {
            signal,
            store,
            onUpdate: (next) =>
                onProgress({ stage: jobProgressLabel(next), stageName: jobStateLabel(next), detachable: true }),
        });
        if (job === null) return { success: false, message: texts.goneText };
        return jobWaitOutcome(job, store, { actions: resultActions });
    };
